import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return context;
};

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    primary: {
      main: '#1565C0',
      light: '#42A5F5',
      dark: '#0D47A1',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0288D1',
      light: '#03A9F4',
      dark: '#01579B',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2E7D32',
      light: '#4CAF50',
      dark: '#1B5E20',
    },
    warning: {
      main: '#ED6C02',
      light: '#FF9800',
      dark: '#E65100',
    },
    error: {
      main: '#D32F2F',
      light: '#EF5350',
      dark: '#C62828',
    },
    info: {
      main: '#0288D1',
      light: '#03A9F4',
      dark: '#01579B',
    },
    background: {
      default: mode === 'light' ? '#F5F7FA' : '#0A1929',
      paper: mode === 'light' ? '#FFFFFF' : '#132F4C',
    },
    text: {
      primary: mode === 'light' ? '#1A1A2E' : '#FFFFFF',
      secondary: mode === 'light' ? '#546E7A' : '#B2BAC2',
    },
    divider: mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)',
    action: {
      hover: mode === 'light' ? 'rgba(21, 101, 192, 0.04)' : 'rgba(66, 165, 245, 0.08)',
      selected: mode === 'light' ? 'rgba(21, 101, 192, 0.08)' : 'rgba(66, 165, 245, 0.16)',
      focus: mode === 'light' ? 'rgba(21, 101, 192, 0.12)' : 'rgba(66, 165, 245, 0.24)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 600, lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '1.75rem', fontWeight: 500, lineHeight: 1.4 },
    h4: { fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.4 },
    h5: { fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.5 },
    h6: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 500 },
    body1: { fontSize: '1rem', lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', lineHeight: 1.57 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: mode === 'light' ? '#F5F7FA' : '#0A1929',
          scrollbarColor: mode === 'light' ? '#C4C4C4 #F5F7FA' : '#3E5060 #0A1929',
          '&::-webkit-scrollbar': { width: '8px', height: '8px' },
          '&::-webkit-scrollbar-track': { background: mode === 'light' ? '#F5F7FA' : '#0A1929' },
          '&::-webkit-scrollbar-thumb': {
            background: mode === 'light' ? '#C4C4C4' : '#3E5060',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': { background: mode === 'light' ? '#A0A0A0' : '#5A6F80' },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8, padding: '8px 20px', fontSize: '0.875rem', fontWeight: 500 },
        contained: {
          boxShadow: '0px 2px 4px rgba(21, 101, 192, 0.2)',
          '&:hover': { boxShadow: '0px 4px 8px rgba(21, 101, 192, 0.3)' },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          '&:hover': { background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)' },
        },
        outlined: { borderWidth: '1.5px', '&:hover': { borderWidth: '1.5px' } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: mode === 'light' ? '#FFFFFF' : '#132F4C',
          borderRadius: 12,
          border: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.06)' : '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: mode === 'light' ? '0px 2px 8px rgba(0, 0, 0, 0.06)' : '0px 2px 8px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: mode === 'light' ? '#FFFFFF' : '#132F4C',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? '#FFFFFF' : '#132F4C',
          color: mode === 'light' ? '#1A1A2E' : '#FFFFFF',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: mode === 'light' ? '#FFFFFF' : '#0A1929',
          borderRight: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.06)' : '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.06)' : '1px solid rgba(255, 255, 255, 0.08)' },
        head: {
          backgroundColor: mode === 'light' ? '#F8FAFC' : '#1A3A5C',
          fontWeight: 600,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: mode === 'light' ? 'rgba(21, 101, 192, 0.04)' : 'rgba(66, 165, 245, 0.08)' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
        colorPrimary: { backgroundColor: 'rgba(21, 101, 192, 0.1)', color: '#42A5F5' },
        colorSuccess: { backgroundColor: 'rgba(46, 125, 50, 0.1)', color: '#4CAF50' },
        colorError: { backgroundColor: 'rgba(211, 47, 47, 0.1)', color: '#EF5350' },
        colorWarning: { backgroundColor: 'rgba(237, 108, 2, 0.1)', color: '#FF9800' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: mode === 'light' ? '#FFFFFF' : '#1A3A5C',
            '& fieldset': { borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.23)' },
            '&:hover fieldset': { borderColor: '#1565C0' },
            '&.Mui-focused fieldset': { borderColor: '#1565C0' },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { backgroundColor: mode === 'light' ? '#FFFFFF' : '#1A3A5C' },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#1565C0', height: 3, borderRadius: '3px 3px 0 0' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, '&.Mui-selected': { color: '#42A5F5' } },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: mode === 'light' ? '#1A1A2E' : '#132F4C',
          fontSize: '0.75rem',
          padding: '8px 12px',
          borderRadius: 6,
        },
        arrow: { color: mode === 'light' ? '#1A1A2E' : '#132F4C' },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.06)' : '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: 12,
          backgroundColor: mode === 'light' ? '#FFFFFF' : '#132F4C',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: mode === 'light' ? '#F8FAFC' : '#1A3A5C',
            borderBottom: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.06)' : '1px solid rgba(255, 255, 255, 0.08)',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.04)' : '1px solid rgba(255, 255, 255, 0.04)',
          },
        },
      },
    },
  },
});

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    return saved || 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  const value = {
    mode,
    toggleTheme,
    isDark: mode === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
